<div class="arcade-shell" [class.arcade-shell--fullscreen]="isFullscreen">
	<div class="arcade-marquee" aria-hidden="true">
		<span class="marquee-text">{{ title }}</span>
	</div>

	<div
		class="scorched-root"
		#gameRoot
		tabindex="0"
		role="application"
		aria-label="Scorched Earth artillery game"
		(keydown)="onKeyDown($event)"
	>
		<div class="hud-row" aria-live="polite">
			<div class="hud-chip">
				<span class="hud-label">Round</span>
				<span class="hud-value">{{ state.round }} / best {{ state.bestOf }}</span>
			</div>
			<div class="hud-chip">
				<span class="hud-label">Environment</span>
				<span class="hud-value hud-value--small">{{ timeLabel }} · {{ weatherLabel }}</span>
			</div>
			<div class="hud-chip hud-chip--grow">
				<span class="hud-label">Status</span>
				<span class="hud-value hud-value--small">{{ state.message }}</span>
			</div>
			<div class="hud-chip" *ngIf="currentPlayer as cp">
				<span class="hud-label">Turn</span>
				<span class="hud-value" [style.color]="cp.color">{{ cp.name }}</span>
			</div>
			<button
				type="button"
				class="hud-fs-btn"
				(click)="toggleFullscreen()"
				[attr.aria-pressed]="isFullscreen"
				[attr.aria-label]="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
			>
				{{ isFullscreen ? 'Exit FS' : 'Fullscreen' }}
			</button>
		</div>

		<div class="battle-layout">
			<div class="canvas-wrap">
				<canvas #gameCanvas class="game-canvas" aria-label="Battlefield canvas"></canvas>

				<div class="overlay" *ngIf="showOverlay">
					<div class="overlay-card" [class.overlay-card--danger]="state.phase === 'gameOver'">
						<ng-container [ngSwitch]="state.phase">
							<ng-container *ngSwitchCase="'setup'">
								<h2 class="overlay-title">Tank Battle Setup</h2>
								<label class="setup-label" for="player-count">Players</label>
								<div class="player-count-row" id="player-count">
									<button type="button" class="chip-btn" [class.chip-btn--active]="playerCount === 2" (click)="onPlayerCountChange(2)">2</button>
									<button type="button" class="chip-btn" [class.chip-btn--active]="playerCount === 3" (click)="onPlayerCountChange(3)">3</button>
									<button type="button" class="chip-btn" [class.chip-btn--active]="playerCount === 4" (click)="onPlayerCountChange(4)">4</button>
								</div>
								<label class="setup-label">Match length</label>
								<div class="player-count-row" aria-label="Match length">
									<button type="button" class="chip-btn" *ngFor="let rounds of [1, 3, 5, 7]" [class.chip-btn--active]="state.bestOf === rounds" (click)="onBestOfChange(rounds)">Best of {{ rounds }}</button>
								</div>
								<div class="name-grid combatant-grid">
									<div class="name-field combatant-card" *ngFor="let p of state.players; trackBy: trackPlayer">
										<label class="setup-label" [attr.for]="'player-name-' + p.id">Combatant {{ p.id + 1 }}</label>
										<input [id]="'player-name-' + p.id" type="text" class="name-input" [value]="p.name" [attr.aria-label]="'Name for ' + p.name" (input)="onPlayerNameChange(p.id, $event)" />
										<div class="player-count-row" [attr.aria-label]="'Control type for ' + p.name">
											<button type="button" class="chip-btn" [class.chip-btn--active]="p.kind === 'human'" (click)="onCombatantKindChange(p.id, 'human')">Human</button>
											<button type="button" class="chip-btn" [class.chip-btn--active]="p.kind === 'cpu'" (click)="onCombatantKindChange(p.id, 'cpu')">Computer</button>
										</div>
										<div class="player-count-row" *ngIf="p.kind === 'cpu'" [attr.aria-label]="'Difficulty for ' + p.name">
											<button type="button" class="chip-btn" *ngFor="let level of ['easy', 'normal', 'hard']" [class.chip-btn--active]="p.difficulty === level" (click)="onCpuDifficultyChange(p.id, $any(level))">{{ level | titlecase }}</button>
										</div>
										<p class="personal-best" *ngIf="personalBest(p.name) as pb">Personal best: {{ pb.score }} (W{{ pb.roundWins }})</p>
									</div>
								</div>
								<div class="leaderboard" *ngIf="localBestEntries.length" aria-label="Local best scores">
									<h3 class="leaderboard-title">Local Bests</h3>
									<p class="leaderboard-rule">{{ scoreRuleLabel }}</p>
									<ol class="leaderboard-list">
										<li class="leaderboard-row" *ngFor="let entry of localBestEntries">
											<span class="leaderboard-name">{{ entry.name }}</span>
											<span class="leaderboard-meta">W{{ entry.roundWins }} · {{ entry.credits }} cr</span>
											<span class="leaderboard-score">{{ entry.score }}</span>
										</li>
									</ol>
								</div>
								<button type="button" class="overlay-button" (click)="startBattle()">Start Battle</button>
							</ng-container>

							<ng-container *ngSwitchCase="'shop'">
								<h2 class="overlay-title">Round Shop</h2>
								<p class="overlay-copy">Spend credits on ammo. Each player buys for themselves.</p>
								<div class="shop-grid">
									<div class="shop-player" *ngFor="let p of state.players; trackBy: trackPlayer">
										<h3 class="shop-name" [style.color]="p.color">{{ p.name }} · {{ p.credits }} cr</h3>
										<div class="shop-weapons">
											<button
												type="button"
												class="shop-btn"
												*ngFor="let w of weapons; trackBy: trackWeapon"
												[disabled]="!p.alive || p.credits < w.shopCost"
												(click)="buy(p.id, w.id)"
											>
												{{ w.shortLabel }} ({{ w.shopCost }})
											</button>
										</div>
									</div>
								</div>
								<button type="button" class="overlay-button" (click)="leaveShop()">Next Round</button>
							</ng-container>

							<ng-container *ngSwitchCase="'roundEnd'">
								<h2 class="overlay-title">Round Over</h2>
								<p class="overlay-copy">{{ state.message }}</p>
								<div class="leaderboard" aria-label="Current match standings">
									<h3 class="leaderboard-title">Standings</h3>
									<p class="leaderboard-rule">{{ scoreRuleLabel }}</p>
									<ol class="leaderboard-list">
										<li class="leaderboard-row" *ngFor="let row of matchStandings">
											<span class="leaderboard-rank">#{{ row.rank }}</span>
											<span class="leaderboard-name" [style.color]="row.color">{{ row.name }}</span>
											<span class="leaderboard-meta">W{{ row.roundWins }} · {{ row.credits }} cr</span>
											<span class="leaderboard-score">{{ row.score }}</span>
										</li>
									</ol>
								</div>
								<button type="button" class="overlay-button" (click)="openShop()">Open Shop</button>
							</ng-container>

							<ng-container *ngSwitchCase="'gameOver'">
								<h2 class="overlay-title">Game Over</h2>
								<p class="overlay-copy">{{ state.message }}</p>
								<div class="leaderboard" aria-label="Final match standings">
									<h3 class="leaderboard-title">Final Standings</h3>
									<p class="leaderboard-rule">{{ scoreRuleLabel }}</p>
									<ol class="leaderboard-list">
										<li class="leaderboard-row" *ngFor="let row of matchStandings">
											<span class="leaderboard-rank">#{{ row.rank }}</span>
											<span class="leaderboard-name" [style.color]="row.color">{{ row.name }}</span>
											<span class="leaderboard-meta">W{{ row.roundWins }} · {{ row.credits }} cr · {{ row.alive ? row.health + ' HP' : 'out' }}</span>
											<span class="leaderboard-score">{{ row.score }}</span>
										</li>
									</ol>
								</div>
								<div class="leaderboard leaderboard--local" *ngIf="localBestEntries.length" aria-label="Local best scores">
									<h3 class="leaderboard-title">Local Bests</h3>
									<ol class="leaderboard-list">
										<li class="leaderboard-row" *ngFor="let entry of localBestEntries">
											<span class="leaderboard-name">{{ entry.name }}</span>
											<span class="leaderboard-score">{{ entry.score }}</span>
										</li>
									</ol>
								</div>
								<button type="button" class="overlay-button" (click)="restart()">Restart</button>
							</ng-container>
						</ng-container>
					</div>
				</div>
			</div>

			<aside class="inventory-rail" *ngIf="showInventoryRail" aria-label="Ammo inventory">
				<div class="inventory-rail__title">Ammo</div>
				<div class="weapon-row" role="toolbar" aria-label="Weapon selection">
					<button
						type="button"
						class="weapon-btn"
						*ngFor="let w of weapons; let i = index; trackBy: trackWeapon"
						[class.weapon-btn--active]="state.selectedWeapon === w.id"
						[disabled]="!isHumanAiming || (currentPlayer?.inventory[w.id] ?? 0) <= 0"
						[attr.aria-label]="w.label + ', ' + (inventoryOwner?.inventory[w.id] ?? 0) + ' remaining'"
						(click)="selectWeapon(w.id)"
					>
						<span class="weapon-key">{{ i + 1 }}</span>
						<span class="weapon-name">{{ w.shortLabel }}</span>
						<span class="weapon-ammo">×{{ inventoryOwner?.inventory[w.id] ?? 0 }}</span>
					</button>
				</div>
			</aside>
		</div>

		<div class="player-strip" aria-label="Player status">
			<div
				class="player-card"
				*ngFor="let p of state.players; trackBy: trackPlayer"
				[class.player-card--active]="p.id === currentPlayer?.id && state.phase === 'aiming'"
				[class.player-card--cpu]="p.kind === 'cpu'"
				[class.player-card--dead]="!p.alive"
			>
				<span class="player-dot" [style.background]="p.color"></span>
				<span class="player-name">{{ p.name }}</span>
				<span class="player-kind" [class.player-kind--cpu]="p.kind === 'cpu'">{{ p.kind === 'cpu' ? 'CPU' : 'Human' }}</span>
				<span class="player-hp">{{ p.health }} HP</span>
				<span class="player-wins">W{{ p.roundWins }}</span>
				<span class="player-score">{{ playerScore(p) }}</span>
			</div>
		</div>

		<div class="cpu-turn-banner" *ngIf="isCpuAiming" aria-live="polite">
			<span class="cpu-turn-banner__label">CPU Turn</span>
			<span class="cpu-turn-banner__name" [style.color]="currentPlayer?.color">{{ currentPlayer?.name }}</span>
			<span class="cpu-turn-banner__hint">Computing aim and firing…</span>
		</div>

		<div class="controls" *ngIf="isHumanAiming">
			<div class="control-group">
				<label class="control-label" for="angle-range">Angle {{ currentPlayer?.angle | number:'1.0-0' }}°</label>
				<input
					id="angle-range"
					type="range"
					[min]="minAngle"
					[max]="maxAngle"
					[value]="currentPlayer?.angle"
					(input)="onAngleChange(+$any($event.target).value)"
					[attr.aria-valuemin]="minAngle"
					[attr.aria-valuemax]="maxAngle"
					[attr.aria-valuenow]="currentPlayer?.angle"
				/>
			</div>
			<div class="control-group">
				<label class="control-label" for="power-range">Power {{ currentPlayer?.power | number:'1.0-0' }}</label>
				<input
					id="power-range"
					type="range"
					[min]="minPower"
					[max]="maxPower"
					[value]="currentPlayer?.power"
					(input)="onPowerChange(+$any($event.target).value)"
					[attr.aria-valuemin]="minPower"
					[attr.aria-valuemax]="maxPower"
					[attr.aria-valuenow]="currentPlayer?.power"
				/>
			</div>

			<div class="action-row">
				<button type="button" class="fire-btn" [disabled]="!canFire" (click)="fire()" aria-label="Fire weapon">
					FIRE
				</button>
				<button type="button" class="pass-btn" [disabled]="!canPass" (click)="pass()" aria-label="Pass turn">
					PASS
				</button>
			</div>
		</div>

		<div class="touch-controls" *ngIf="isHumanAiming" aria-label="Touch controls">
			<button type="button" class="touch-btn" aria-label="Decrease angle" (pointerdown)="onTouchAngleDown($event)" (click)="adjustAngle(-3)">∠−</button>
			<button type="button" class="touch-btn" aria-label="Decrease power" (pointerdown)="onTouchPowerDown($event)" (click)="adjustPower(-3)">P−</button>
			<button type="button" class="touch-btn touch-btn--fire" aria-label="Fire" (pointerdown)="onTouchFire($event)" (click)="fire()">FIRE</button>
			<button type="button" class="touch-btn" aria-label="Increase power" (pointerdown)="onTouchPowerUp($event)" (click)="adjustPower(3)">P+</button>
			<button type="button" class="touch-btn" aria-label="Increase angle" (pointerdown)="onTouchAngleUp($event)" (click)="adjustAngle(3)">∠+</button>
		</div>

		<p class="control-hint">
			Arrows / WASD aim · Space fire · P pass when out of ammo · Tab cycle weapons · 1–6 select · F fullscreen · Enter confirm menus
		</p>
	</div>
</div>
